import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary-500 uppercase tracking-widest mb-3">
              AIVO SOLUTION SDN BHD
            </p>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              Terms of Service
            </h1>
            <p className="mt-3 text-sm text-foreground-500">Last updated: July 7, 2026</p>
          </div>

          <div className="prose prose-foreground max-w-none space-y-6 text-sm text-foreground-700 leading-relaxed">
            <p>
              Welcome to <strong>ggcourse</strong>, operated by <strong>AIVO SOLUTION SDN BHD</strong> (Registration No. 202401012345). By accessing or using our website at ggcourse.com and purchasing our courses, you agree to be bound by these Terms of Service. Please read them carefully before using our services.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">1. Definitions</h2>
            <ul className="space-y-1">
              <li><strong>&quot;Company,&quot;</strong> <strong>&quot;We,&quot;</strong> <strong>&quot;Us,&quot;</strong> or <strong>&quot;Our&quot;</strong> refers to AIVO SOLUTION SDN BHD.</li>
              <li><strong>&quot;Platform&quot;</strong> or <strong>&quot;Website&quot;</strong> refers to ggcourse.com and all associated subdomains.</li>
              <li><strong>&quot;Services&quot;</strong> refers to the online courses, educational materials, and related content provided through our Platform.</li>
              <li><strong>&quot;User,&quot;</strong> <strong>&quot;You,&quot;</strong> or <strong>&quot;Your&quot;</strong> refers to any individual who accesses or uses our Platform.</li>
            </ul>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">2. Acceptance of Terms</h2>
            <p>
              By creating an account, making a purchase, or otherwise using our Services, you confirm that you are at least 18 years of age (or have obtained parental consent) and that you agree to these Terms. If you do not agree with any part of these Terms, you must discontinue use of our Platform immediately.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">3. Account Registration</h2>
            <p>
              To access certain features, you may be required to create an account. You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You agree to provide accurate, current, and complete information during registration and to update such information as necessary.
            </p>
            <p>
              Sharing your account credentials with third parties is strictly prohibited. We reserve the right to suspend or terminate accounts that violate this provision.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">4. Course Access &amp; License</h2>
            <p>
              Upon successful purchase, you are granted a <strong>non-exclusive, non-transferable, revocable license</strong> to access and view the course content for personal, non-commercial use only. This license does not grant you the right to:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Reproduce, distribute, or publicly display any course materials.</li>
              <li>Create derivative works based on course content.</li>
              <li>Use course content for commercial training or teaching purposes.</li>
              <li>Share your account access with any other person.</li>
            </ul>
            <p>All course content, including videos, text, images, and code samples, remains the intellectual property of AIVO SOLUTION SDN BHD and its instructors.</p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">5. Payment &amp; Pricing</h2>
            <p>
              All prices are listed in <strong>United States Dollars (USD)</strong> unless otherwise stated. We reserve the right to modify prices at any time without prior notice; however, price changes will not affect purchases already completed. You are responsible for any applicable taxes, duties, or currency conversion fees imposed by your jurisdiction or payment provider.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">6. No Refund Policy</h2>
            <p>
              All sales are final and non-refundable. By making a purchase, you acknowledge that our courses are digital products delivered instantly upon purchase and are therefore non-refundable. For full details, please refer to our <Link to="/refund-policy" className="text-primary-500 hover:text-primary-600 underline">Refund Policy</Link>, which is incorporated into these Terms by reference.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">7. Intellectual Property</h2>
            <p>
              All content on the Platform, including but not limited to text, graphics, logos, icons, images, audio clips, video content, software, and course materials, is the exclusive property of AIVO SOLUTION SDN BHD or its content suppliers and is protected by Malaysian and international copyright laws. The <strong>ggcourse</strong> name and logo are trademarks of AIVO SOLUTION SDN BHD.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">8. User Conduct</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Use the Platform for any unlawful purpose or in violation of any applicable laws.</li>
              <li>Attempt to gain unauthorized access to any part of the Platform or its related systems.</li>
              <li>Interfere with or disrupt the Platform or servers hosting the Platform.</li>
              <li>Upload or transmit any malicious code, viruses, or harmful components.</li>
              <li>Harass, abuse, or harm other users or instructors.</li>
              <li>Use automated means (bots, scrapers, etc.) to access or collect data from the Platform without our express written permission.</li>
            </ul>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">9. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, AIVO SOLUTION SDN BHD shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, or goodwill, arising out of or in connection with your use of the Platform or Services, whether based on warranty, contract, tort, or any other legal theory.
            </p>
            <p>
              Our total liability for any claim arising from your use of the Platform shall not exceed the amount you paid for the specific course giving rise to the claim during the twelve (12) months preceding the claim.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">10. Disclaimer of Warranties</h2>
            <p>
              The Platform and all Services are provided on an <strong>&quot;as is&quot;</strong> and <strong>&quot;as available&quot;</strong> basis without any warranties of any kind, either express or implied. We do not warrant that the Platform will be uninterrupted, error-free, secure, or free from viruses or other harmful components.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">11. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account and access to the Platform at our sole discretion, without prior notice, for any conduct that we believe violates these Terms or is harmful to other users, us, or third parties.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of <strong>Malaysia</strong>. Any disputes arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the courts of Kuala Lumpur, Malaysia.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">13. Changes to These Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on this page, and the &quot;Last updated&quot; date will be revised accordingly. Your continued use of the Platform after any changes constitutes acceptance of the revised Terms. We encourage you to review this page periodically.
            </p>

            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-3">14. Contact Information</h2>
            <p>For questions about these Terms of Service, please contact:</p>
            <p>
              <strong>AIVO SOLUTION SDN BHD</strong><br />
              Email: admin@ggcourse.com<br />
              Address: Level 8, Tower A, Menara UOA Bangsar, No. 5 Jalan Bangsar Utama 1, 59000 Kuala Lumpur, Malaysia
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}